import { LitElement, css } from "lit-element";
import { html } from "lit-html";
import { storeInstance } from "store/index.js";

/**
 * Allows users to opt out of google analytics
 * Reference: https://github.com/googleanalytics/ga4-tutorials/blob/main/src/public/index.css
 * @property {Boolean} showBanner - hide/show banner
 * @property {Object} consentMode - contains privacy settings
 * @property {Object} kbUser - currently logged in user
 * @property {String} authMethod - currently selected authMode - Login | SSO | Auto
 */
class KbConsentFooter extends LitElement {
  static get properties() {
    return {
      showBanner: { type: Boolean },
      consentMode: { type: Object },
      kbUser: { type: Object },
      authMethod: { type: String },
    };
  }

  constructor() {
    super();
    this.consentMode = localStorage.getItem("consentMode");
  }

  connectedCallback() {
    super.connectedCallback();
    this.subscription = storeInstance.subscribe((state) => {
      this.stateChange(state);
    });
    this.state = storeInstance.state;
  }

  stateChange(state) {
    this.state = state;
    this.kbUser = this.state.kb_user;
    this.authMode = this.state.auth_method;
  }

  updated(changedProperties) {
    if (
      changedProperties.has("consentMode") ||
      changedProperties.has("kbUser")
    ) {
      this.showBanner = this.shouldShowBanner();
    }
  }

  shouldShowBanner() {
    // console.log("GTM", window.google_tag_manager);
    let hasTagManager =
      typeof window.google_tag_manager === "object" &&
      window.google_tag_manager !== null;
    let hasNotSetConsent =
      this.consentMode == null || this.hasContainerIdChanged();
    let hasLoggedIn = this.kbUser?.username || this.authMode === "Auto";
    return hasTagManager && hasNotSetConsent && hasLoggedIn;
  }

  hasContainerIdChanged() {
    let currentId = this.getContainerId();
    let consentMode = JSON.parse(this.consentMode);
    let oldId = consentMode ? consentMode.containerId : null;
    // console.log("hasContainerId", currentId, oldId);
    return currentId != oldId;
  }

  getContainerId() {
    const hasGTM =
      typeof window.google_tag_manager === "object" &&
      window.google_tag_manager !== null;
    if (!hasGTM) return null;
    let gtm = window.google_tag_manager;
    const keys = Object.keys(gtm);
    const id = keys.find((key) => /^GTM/.test(key) || /^[A-Z0-9]+$/.test(key));
    if (!id) return null;
    return id;
  }

  setConsent(consent) {
    const consentMode = {
      containerId: this.getContainerId(),
      functionality_storage: consent.necessary ? "granted" : "denied",
      security_storage: consent.necessary ? "granted" : "denied",
      ad_storage: consent.marketing ? "granted" : "denied",
      analytics_storage: consent.analytics ? "granted" : "denied",
      personalization: consent.preferences ? "granted" : "denied",
    };
    gtag("consent", "update", consent);
    localStorage.setItem("consentMode", JSON.stringify(consentMode));
  }

  hideBanner() {
    this.showBanner = false;
  }

  accept() {
    this.setConsent({
      necessary: true,
      analytics: this.shadowRoot.querySelector("#consent-analytics").checked,
      preferences: false,
      marketing: false,
    });
    this.hideBanner();
  }

  reject() {
    this.setConsent({
      necessary: true,
      analytics: false,
      preferences: false,
      marketing: false,
    });
    this.hideBanner();
  }

  bannerTemplate() {
    return html`
      <div id="cookie-consent-banner" class="cookie-consent-banner">
        <h3>Cookie settings</h3>
        <p>
          We use cookies to provide you with the best possible experience. They
          also allow us to analyze user behavior in order to constantly improve
          the website for you.
        </p>
        <button
          @click=${this.accept}
          id="btn-accept"
          class="cookie-consent-button btn-outline"
        >
          Accept
        </button>
        <button
          @click=${this.reject}
          id="btn-reject-all"
          class="cookie-consent-button btn-grayscale"
        >
          Reject
        </button>
        <div class="cookie-consent-options">
          <label
            ><input
              id="consent-necessary"
              type="checkbox"
              value="Necessary"
              checked
              disabled
            />Necessary</label
          >
          <label
            ><input
              id="consent-analytics"
              type="checkbox"
              value="Analytics"
              checked
            />Analytics</label
          >
        </div>
      </div>
    `;
  }

  render() {
    return html` ${this.showBanner ? this.bannerTemplate() : null}`;
  }

  static get styles() {
    return css`
      .divider {
        height: 1rem;
        background-color: rgba(0, 0, 0, 0.1);
        border: solid rgba(0, 0, 0, 0.15);
        border-width: 1px 0;
        box-shadow: inset 0 0.5em 1.5em rgba(0, 0, 0, 0.1);
        inset: 0 0.125em 0.5em rgba(0, 0, 0, 0.15);
      }

      .cookie-consent-banner {
        display: block;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background-color: #f8f9fa;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        color: black;
        padding: 15px;
        font-size: 14px;
        z-index: 1000;
      }

      .cookie-consent-button {
        border: none;
        padding: 8px 16px;
        text-decoration: none;
        display: inline-block;
        font-size: 14px;
        margin: 4px 2px;
        cursor: pointer;
        border-radius: 4px;
      }

      .cookie-consent-button:hover {
        box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.2);
      }

      .cookie-consent-button:active {
        opacity: 0.5;
      }

      .cookie-consent-button.btn-success {
        background-color: #34a853;
        color: white;
      }

      .cookie-consent-button.btn-grayscale {
        background-color: #dfe1e5;
        color: black;
      }

      .cookie-consent-button.btn-outline {
        background-color: #e6f4ea;
        color: #34a853;
      }

      .cookie-consent-options {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 10px;
      }

      .cookie-consent-options label {
        margin: 0 10px;
        font-size: 14px;
      }

      .cookie-consent-options input {
        margin-right: 5px;
      }
    `;
  }
}

customElements.define("kb-consent-footer", KbConsentFooter);
